// Period / vehicle / chart granularity part of the URL state, common to every owner reporting page (earnings, dashboard).
// Pure functions: no React, no browser. Page specific parts (tabs, sort, search, page) live in their own module.

export const PERIODS = ['today', '7d', '30d', 'year', 'custom'];
export const GRANULARITIES = ['day', 'week', 'month', 'year'];
export const FILTER_KEYS = ['period', 'startDate', 'endDate', 'listId', 'granularity'];

export const DEFAULT_FILTERS = {
  period: '30d',
  startDate: '',
  endDate: '',
  listId: null,
  granularity: 'month',
};

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export const firstValue = (value) => (Array.isArray(value) ? value[0] : value);
export const oneOf = (value, allowed, fallback) => (allowed.includes(value) ? value : fallback);
export const positiveInt = (value) => {
  const number = Number(firstValue(value));
  return Number.isInteger(number) && number > 0 ? number : null;
};
export const validDate = (value) => {
  const text = String(firstValue(value) || '');
  if (!DATE_PATTERN.test(text)) return '';
  const date = new Date(`${text}T00:00:00Z`);
  return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== text ? '' : text;
};

// A route query object, a query string or a URLSearchParams -> plain object (first value of each key).
export function toQueryObject(input) {
  if (typeof input === 'string' || (typeof URLSearchParams !== 'undefined' && input instanceof URLSearchParams)) {
    const query = {};
    new URLSearchParams(input).forEach((value, key) => { if (!(key in query)) query[key] = value; });
    return query;
  }
  return input || {};
}

export function parseFilters(input) {
  const query = toQueryObject(input);
  let period = oneOf(firstValue(query.period), PERIODS, DEFAULT_FILTERS.period);
  const startDate = validDate(query.startDate);
  const endDate = validDate(query.endDate);
  if (period === 'custom' && (!startDate || !endDate || endDate < startDate)) period = DEFAULT_FILTERS.period;
  return {
    period,
    startDate: period === 'custom' ? startDate : '',
    endDate: period === 'custom' ? endDate : '',
    listId: positiveInt(query.listId),
    granularity: oneOf(firstValue(query.granularity), GRANULARITIES, DEFAULT_FILTERS.granularity),
  };
}

// Writes the non-default filters into a URLSearchParams.
export function addFilterParams(params, filters, omit = []) {
  const current = { ...DEFAULT_FILTERS, ...filters };
  const add = (key, value) => { if (!omit.includes(key)) params.set(key, String(value)); };
  if (current.period !== DEFAULT_FILTERS.period) add('period', current.period);
  if (current.period === 'custom') { add('startDate', current.startDate); add('endDate', current.endDate); }
  if (current.listId) add('listId', current.listId);
  if (current.granularity !== DEFAULT_FILTERS.granularity) add('granularity', current.granularity);
  return params;
}

// Dates only make sense for a custom period.
export function normalizeFilters(state) {
  if (state.period === 'custom') return state;
  return { ...state, startDate: '', endDate: '' };
}
